// Plan hierarchy and helper functions for subscription management

export const planLevels = {
  'none': 0,
  'basis': 1,
  'basis_year': 1,
  'komfort': 2,
  'komfort_year': 2,
  'premium': 3,
  'premium_year': 3,
  'company_basic': 4,
  'company_basic_year': 4,
  'company_multi_email': 5,
  'company_multi_email_year': 5,
  'company_pro': 6,
  'company_pro_year': 6,
  'admin_unlimited': 99
} as const;

export type PlanType = keyof typeof planLevels;

/** Add-On plan IDs that run parallel to the main subscription */
export const addonPlanIds = [
  'phishing_basic',
  'phishing_premium',
  'email_check',
] as const;

export type AddonPlanId = typeof addonPlanIds[number];

/**
 * Checks if a plan_id is an add-on (runs parallel, not a main subscription)
 */
export const isAddonPlan = (planId: string): boolean => {
  return (addonPlanIds as readonly string[]).includes(planId);
};

/**
 * Extracts the base plan name without period suffix
 * @example getBasePlan('basis_year') => 'basis'
 * @example getBasePlan('komfort') => 'komfort'
 */
export const getBasePlan = (plan: string): string => {
  return plan.replace(/_year$/, '');
};

/**
 * Gets the billing period of a plan
 * @returns 'year' | 'month'
 */
export const getPeriod = (plan: string): 'month' | 'year' => {
  return plan.endsWith('_year') ? 'year' : 'month';
};

/**
 * Checks if two plans are the same tier (ignoring billing period)
 * @example isSameTier('basis', 'basis_year') => true
 * @example isSameTier('basis', 'komfort') => false
 */
export const isSameTier = (currentPlan: string, newPlan: string): boolean => {
  return getBasePlan(currentPlan) === getBasePlan(newPlan);
};

/**
 * Checks if the change is only a period change (month <-> year) without tier change
 * @example isPeriodChange('basis', 'basis_year') => true
 * @example isPeriodChange('basis', 'komfort') => false
 */
export const isPeriodChange = (currentPlan: string, newPlan: string): boolean => {
  return isSameTier(currentPlan, newPlan) && getPeriod(currentPlan) !== getPeriod(newPlan);
};

export const isUpgrade = (currentPlan: string, newPlan: string): boolean => {
  const currentLevel = planLevels[currentPlan as PlanType] ?? 0;
  const newLevel = planLevels[newPlan as PlanType] ?? 0;
  return newLevel > currentLevel;
};

export const isDowngrade = (currentPlan: string, newPlan: string): boolean => {
  const currentLevel = planLevels[currentPlan as PlanType] ?? 0;
  const newLevel = planLevels[newPlan as PlanType] ?? 0;
  return newLevel < currentLevel && currentLevel > 0;
};

export const isSamePlan = (currentPlan: string, newPlan: string): boolean => {
  return currentPlan === newPlan;
};

export const getPlanAction = (currentPlan: string, newPlan: string): 'same' | 'upgrade' | 'downgrade' | 'new' | 'period_change' => {
  if (!currentPlan || currentPlan === 'none') return 'new';
  if (isSamePlan(currentPlan, newPlan)) return 'same';
  if (isPeriodChange(currentPlan, newPlan)) return 'period_change';
  if (isUpgrade(currentPlan, newPlan)) return 'upgrade';
  return 'downgrade';
};

/** Company plan IDs including the new Company Pro tier */
export const companyPlanIds = [
  'company_basic', 'company_basic_year',
  'company_multi_email', 'company_multi_email_year',
  'company_pro', 'company_pro_year',
] as const;

/**
 * Checks if a plan is any company plan
 */
export const isCompanyPlan = (planId: string): boolean => {
  return (companyPlanIds as readonly string[]).includes(planId);
};
